// Column layout for the Voice Validation CSV export: one row per voice, with the
// accounts of each social platform collapsed into a single cell so a voice never
// spans multiple rows. Demographics are intentionally out of scope.
const SOCIAL_PLATFORMS = [
	['instagram', 'Instagram'],
	['tiktok', 'TikTok'],
	['twitter', 'Twitter/X'],
	['youtube', 'YouTube'],
	['facebook', 'Facebook'],
	['wechat_public', 'Wechat'],
	['weibo', 'Weibo'],
	['douyin', 'Douyin'],
	['xiaohongshu', 'Xiaohongshu'],
	['bilibili', 'Bilibili']
];

const MEDIA_TYPES = [
	['online', 'Online Media'],
	['print', 'Print Media']
];

const ACCOUNT_SEPARATOR = ' | ';

function formatAccounts(profiles) {
	return (profiles || [])
		.map(profile => {
			const handle = profile.screen_name || profile.name || profile.id;
			return profile.profile_url ? `${handle} (${profile.profile_url})` : String(handle ?? '');
		})
		.filter(Boolean)
		.join(ACCOUNT_SEPARATOR);
}

// Print media has no URL, so media entries fall back to the media id as their identifier.
function formatMedias(medias) {
	return (medias || [])
		.map(media => {
			const name = media.name || `#${media.id}`;
			return media.url ? `${name} (${media.url})` : `${name} (#${media.id})`;
		})
		.filter(Boolean)
		.join(ACCOUNT_SEPARATOR);
}

// Dates arrive as ISO strings over JSON; keep them sortable but drop the milliseconds.
function formatDate(value) {
	if (!value) return '';
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? String(value) : date.toISOString().replace(/\.\d+Z$/, 'Z');
}

function escapeCsvCell(value) {
	const str = value == null ? '' : String(value);
	// Spreadsheets evaluate cells starting with these characters as formulas.
	const safe = /^[=+\-@\t\r]/.test(str) ? `'${str}` : str;
	return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function buildVoicesCsv(voices) {
	const headers = [
		'Voice ID',
		'Voice Name',
		'Type',
		'Status',
		'Validated',
		'Country Code',
		'Country Name',
		'Last Edited By',
		'Inserted At',
		'Updated At',
		...SOCIAL_PLATFORMS.map(([, label]) => label),
		...MEDIA_TYPES.map(([, label]) => label)
	];
	const rows = voices.map(voice => [
		voice.id,
		voice.name,
		voice.type ?? '',
		voice.status ?? '',
		voice.validated ? 'TRUE' : 'FALSE',
		voice.country?.id ?? '',
		voice.country?.name ?? '',
		voice.last_edited_by ?? '',
		formatDate(voice.inserted_at),
		formatDate(voice.updated_at),
		...SOCIAL_PLATFORMS.map(([key]) => formatAccounts(voice.socialmedia?.[key])),
		...MEDIA_TYPES.map(([key]) => formatMedias(voice.medias?.[key]))
	]);
	return [headers, ...rows]
		.map(row => row.map(escapeCsvCell).join(','))
		.join('\r\n');
}

export function downloadCsv(filename, csv) {
	// The BOM makes Excel read the file as UTF-8; voice names are frequently CJK.
	const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
}
